import { Component } from 'react';
import { Link } from 'react-router';
import { connect } from 'react-redux';
import lodash from 'lodash';

import icons from 'styles/icons/binder-icon.scss';
import styles from './Details.scss';
import DateTime from 'common/components/DateTime/DateTime';
import {
    UserCanEditBinderDetails
} from 'common/permissions/components/sidebar';
import {
    UserCanUploadFiles
} from 'common/permissions/components/actionButton';
import {
    binderEmailDomainSelector
} from 'common/selectors/index';
import { isIntegrationToggleEnabled, partnerName } from 'common/actions/integrationActions';
import CopyLink from 'components/common/CopyLink/CopyLink';

class Details extends Component {
    render() {
        const { binder, users, binderEmailDomain, binderPermissions } = this.props;
        if (lodash.isNil(binder)) {
            return null;
        }

        return (
            <div className={styles.detailsContainer} data-cy="Details">
                <header className={styles.header}>
                    <span className={icons.binderIcon} />
                    <span className={styles.title} data-cy="Details-title">
                        {binder.title}
                    </span>
                    <EditLinkComponent to={`/binder/${binder.id}/edit`} />
                </header>
                <BinderDetails binder={binder} binderEmailDomain={binderEmailDomain} />
                {binderPermissions.viewBinderUsers && (
                    <section className={styles.users} data-cy="Details-viewBinderUsers">
                        <header className={styles.subtitle}>Users</header>
                        <UsersSection users={users} />
                    </section>
                )}
            </div>
        );
    }
}

const EditLinkComponent = UserCanEditBinderDetails(({ to }) => (
    <Link className={styles.editLink} to={to}>
        Edit Binder
    </Link>
));

const BinderDetails = ({ binder, binderEmailDomain }) => {
    let createdBy = 'n/a';
    let emailAddress = 'n/a';
    if (!lodash.isNil(binder.createdBy)) {
        createdBy = binder.createdBy.displayName;
        emailAddress = binder.createdBy.emailAddress;
    }

    const binderDetails = [{
        label: 'Description',
        value: binder.description,
        type: 'text',
    }, {
        label: 'Trial Type',
        value: binder.trialType,
        type: 'text',
    }, {
        label: 'Principal Investigator',
        value: binder.primaryInvestigator,
        type: 'text',
    }, {
        label: 'IRB Number',
        value: binder.irbNumber,
        type: 'text',
    }, {
        label: 'Contact',
        value: binder.contact,
        type: 'text',
    }, {
        label: 'Trial ID',
        value: binder.trialId,
        type: 'text',
    }, {
        label: 'Department/Site',
        value: binder.departmentSiteDg,
        type: 'text',
    }, {
        label: 'ClinicalTrials.gov Number',
        value: binder.ctGovNumber,
        type: 'text',
    }, {
        label: 'Sponsor/CRO',
        value: binder.sponsorCro,
        type: 'text',
    }, {
        label: 'Renewal Date',
        value: binder.renewalDate,
        type: 'date-only',
    }, {
        label: 'Binder Status',
        value: binder.status,
        type: 'text',
    }, {
        label: 'Created by',
        value: createdBy,
        type: 'text',
    }, {
        label: 'Email Address',
        value: emailAddress,
        type: 'text',
    }, {
        label: 'Created on',
        value: binder.createdAt,
        type: 'date',
    }, {
        label: 'Binder Forwarding Address',
        value: {
            address: `${binder.id}@${binderEmailDomain}`,
            owner: binder.title,
        },
        type: 'email',
    },
    {
        label: 'Binder URL',
        value: binder.id,
        type: 'link',
    }];

    if (isIntegrationToggleEnabled(binder.integrations, partnerName.VELOS)) {
        binderDetails.push({
            label: 'Velos Integration Study ID',
            value: binder.partnerMetadata.velosStudyId || '',
            type: 'text',
        });
    }

    if (isIntegrationToggleEnabled(binder.integrations, partnerName.CONNEXUS)) {
        binderDetails.push({
            label: 'Connexus Integration Details',
            value: `Study ID: ${binder.partnerMetadata.connexusStudyId || ''}`,
            type: 'text',
        }),
        binderDetails.push({
            label: '',
            value: `Site ID: ${binder.partnerMetadata.connexusSiteId || ''}`,
            type: 'text',
        });
    }

    const renderedBinderDetails = lodash.map(binderDetails, ({ label, value, type }) => {
        return <BinderDetail key={label} label={label} value={value} type={type} />;
    });

    return (
        <section className={styles.details} data-cy="Details-renderedBinderDetails">
            {renderedBinderDetails}
        </section>
    );
};

const EmailLink = UserCanUploadFiles(({ email }) => (
    <a className={styles.detailLink} href={`mailto: ${email.owner} <${email.address}>`} data-cy="Details-address">
        {email.address}
    </a>
));

const binderDetailElementMap = {
    'text': value => value,
    'date': value => <DateTime value={value} applyTimezoneOffset={true} />,
    'date-only': value => <DateTime value={value} applyTimezoneOffset={false} time={false} isUTC={true} />,
    'email': value => <EmailLink email={value} />,
    'link': value => <CopyLink type='binder' id={value} />,
};

const BinderDetail = ({ label, value, type }) => {
    const binderDetailElementBuilder = binderDetailElementMap[type];
    const formattedValue = binderDetailElementBuilder(value);
    return (
        <div className={styles.detail} data-cy="Details-BinderDetail">
            <span className={styles.detailLabel}>{label}</span>
            <p className={styles.detailValue}>{formattedValue}</p>
        </div>
    );
};

export const UsersSection = props => {
    const { users } = props;
    return (
        <div data-cy="Details-UsersSection">
            {lodash.map(users, u => <RenderedUser key={u.sub} user={u} />)}
        </div>
    );
};

export const RenderedUser = props => {
    const { user } = props;
    return (
        <p data-cy="Details-displayName">{user.displayName}</p>
    );
};

export default connect(state => {
    const binder = lodash.get(state, 'binder');
    const users = lodash.get(state, 'binder.users');
    const binderEmailDomain = binderEmailDomainSelector(state);
    const binderPermissions = lodash.get(state, 'me.binderPermissions', {});
    return {
        binder,
        users,
        binderEmailDomain,
        binderPermissions,
    };
})(Details);
